import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

interface TeacherLead {
  _id?: string | { $oid?: string };
  id?: string;
  studentUserId: string | { $oid?: string };
  studentName?: string;
  subject: string;
  subjectId?: string;
  classMode: 'ONLINE' | 'OFFLINE' | 'HYBRID';
  offlineSessionType?: 'HOME_SINGLE' | 'GROUP';
  frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'WEEKENDS';
  preferredTimeSlot?: string;
  notes?: string;
  selectedAt: string;
}

const TeacherLeads: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<TeacherLead[]>([]);
  const [error, setError] = useState<string>('');
  const [subjectLabelMap, setSubjectLabelMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [leadsResponse, subjectsResponse] = await Promise.all([
          api.get<TeacherLead[]>('/teachers/selections/teacher'),
          api.get('/subjects/hierarchy'),
        ]);

        setLeads(Array.isArray(leadsResponse.data) ? leadsResponse.data : []);

        const hierarchy = subjectsResponse.data || {};
        const nextSubjectLabelMap: Record<string, string> = {};
        Object.values(hierarchy).forEach((categories: any) => {
          Object.values(categories || {}).forEach((group: any) => {
            Object.values(group || {}).forEach((items: any) => {
              (Array.isArray(items) ? items : []).forEach((subject: any) => {
                const subjectKey = String(subject?._id || subject?.id || '').trim();
                const primary = String(subject?.displayName || subject?.name || '').trim();
                const details = [subject?.class, subject?.stream, subject?.exam, subject?.activityType]
                  .map((value) => String(value || '').trim())
                  .filter(Boolean)
                  .join(' | ');
                const subjectLabel = details ? `${primary} (${details})` : primary;
                if (subjectKey && subjectLabel) {
                  nextSubjectLabelMap[subjectKey] = subjectLabel;
                }
              });
            });
          });
        });
        setSubjectLabelMap(nextSubjectLabelMap);
        setError('');
      } catch (error) {
        console.error('Failed to load teacher leads:', error);
        setLeads([]);
        setSubjectLabelMap({});
        const message = (error as any)?.response?.data?.message;
        setError(typeof message === 'string' ? message : 'Leads are not enabled yet.');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const summary = useMemo(() => {
    const total = leads.length;
    const online = leads.filter((lead) => lead.classMode === 'ONLINE').length;
    const offline = leads.filter((lead) => lead.classMode === 'OFFLINE').length;
    return { total, online, offline };
  }, [leads]);

  const resolveSubjectLabel = (lead: TeacherLead) => {
    const subjectId = String(lead.subjectId || '').trim();
    const directSubject = String(lead.subject || '').trim();
    return (
      subjectLabelMap[subjectId] ||
      subjectLabelMap[directSubject] ||
      directSubject ||
      'General'
    );
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900">Teacher Leads</h1>
        <p className="mt-1 text-sm text-gray-600">Students who selected you for classes.</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-md bg-blue-50 px-4 py-3">
            <p className="text-xs text-blue-700">Total Leads</p>
            <p className="text-xl font-semibold text-blue-900">{summary.total}</p>
          </div>
          <div className="rounded-md bg-emerald-50 px-4 py-3">
            <p className="text-xs text-emerald-700">Online</p>
            <p className="text-xl font-semibold text-emerald-900">{summary.online}</p>
          </div>
          <div className="rounded-md bg-amber-50 px-4 py-3">
            <p className="text-xs text-amber-700">Offline</p>
            <p className="text-xl font-semibold text-amber-900">{summary.offline}</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Lead List</h2>
        </div>
        {error ? (
          <div className="px-6 py-6 text-sm text-amber-700">{error}</div>
        ) : null}
        {loading ? (
          <div className="px-6 py-6 text-sm text-gray-500">Loading leads...</div>
        ) : !error && leads.length === 0 ? (
          <div className="px-6 py-6 text-sm text-gray-500">No leads yet.</div>
        ) : !error ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Mode</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Offline Class Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Frequency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Selected On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {leads.map((lead, idx) => (
                  <tr key={lead.id || (typeof lead._id === 'string' ? lead._id : `lead-${idx}`)} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{lead.studentName || 'Student'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{resolveSubjectLabel(lead)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{lead.classMode}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {lead.classMode === 'OFFLINE'
                        ? lead.offlineSessionType === 'GROUP'
                          ? 'Group'
                          : 'Home / Single'
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{lead.frequency}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(lead.selectedAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <Link to="/teacher/dashboard" className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
          Back To Dashboard
        </Link>
        <Link to="/teacher/exams" className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Create Exams
        </Link>
        <Link to="/teacher/payments" className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Earnings & Payments
        </Link>
      </div>
    </div>
  );
};

export default TeacherLeads;
